import React, { useState } from 'react';
import Decimal from 'decimal.js';
import { useNavigate } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { CustomerPicker } from '../../customers/components/CustomerPicker';
import { useCreateLead } from '../hooks';
import { DynamicContactList, DynamicAddressList, type ContactItem, type AddressItem } from '@repo/web-form';
import { useStaffList } from '../../staff/network/staff.api';
import { useSchemes } from '../../schemes/network/schemes.api';
import { getSession } from '../../../lib/session';

import { toAppError } from '@repo/api-client';
import { getErrorMessage } from '@repo/i18n';

export function CreateProspectPage({ backTo = '/crm/leads' }: { backTo?: string }) {
  const navigate = useNavigate();
  const session = getSession();
  const currentUserId = session?.user?.id || '';

  const { data: staffList = [] } = useStaffList();
  const { data: schemesResponse } = useSchemes({ status: ['ACTIVE', 'REGISTERED'], limit: 100 });
  const schemes = Array.isArray(schemesResponse?.data) ? schemesResponse.data : [];

  const [leadMode, setLeadMode] = useState<'NEW_PROSPECT' | 'EXISTING_PARTY'>('NEW_PROSPECT');
  
  // Prospect fields
  const [prospectName, setProspectName] = useState('');
  const [prospectGender, setProspectGender] = useState<'M' | 'F' | 'OTHER' | ''>('');
  const [prospectDob, setProspectDob] = useState('');

  // 1-to-Many Dynamic Contacts & Addresses
  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'PHONE', label: 'MOBILE', value: '', isPrimary: true, isWhatsapp: true },
  ]);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);

  // Existing party field
  const [partyId, setPartyId] = useState('');

  // Enterprise Lead Fields
  const [source, setSource] = useState('');
  const [ownerId, setOwnerId] = useState(currentUserId);
  const [productCategory, setProductCategory] = useState('');
  const [productInstanceId, setProductInstanceId] = useState('');
  const [expectedValueRupees, setExpectedValueRupees] = useState('');
  const [initialNote, setInitialNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');

  const [error, setError] = useState<string | null>(null);

  const createLeadMutation = useCreateLead();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!source) {
      setError('Please select a Lead Source (e.g. Branch Walk-in, Inbound Call, Referral).');
      return;
    }

    const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts.find((c) => c.type === 'PHONE')?.value || '';
    const primaryEmail = contacts.find((c) => c.type === 'EMAIL' && c.isPrimary)?.value || contacts.find((c) => c.type === 'EMAIL')?.value || undefined;

    if (leadMode === 'NEW_PROSPECT') {
      if (!prospectName.trim() || prospectName.trim().length < 2) {
        setError('Please enter a valid prospect name (at least 2 characters).');
        return;
      }
      const mobileClean = primaryPhone.trim().replace(/\D/g, '');
      if (!/^[6-9]\d{9}$/.test(mobileClean)) {
        setError('Please add at least one valid 10-digit Indian mobile number.');
        return;
      }
    } else if (leadMode === 'EXISTING_PARTY' && !partyId) {
      setError('Please select an existing customer or party.');
      return;
    }

    try {
      let expectedPaise: number | undefined = undefined;
      if (expectedValueRupees.trim()) {
        try {
          const d = new Decimal(expectedValueRupees.trim());
          if (!d.isNaN() && d.isPositive()) {
            expectedPaise = d.mul(100).toNumber();
          }
          // allow-silent-catch: fallback to undefined if invalid number format entered
        } catch {
          expectedPaise = undefined;
        }
      }

      const validContacts = contacts
        .filter((c) => c.value && c.value.trim() !== '')
        .map((c) => ({
          type: c.type,
          label: c.label || 'PRIMARY',
          value: c.type === 'PHONE' ? c.value.trim().replace(/\D/g, '') : c.value.trim(),
          isPrimary: c.isPrimary,
          isWhatsapp: c.isWhatsapp,
        }));

      const validAddresses = addresses
        .filter((a) => a.addressLine1 && a.city && a.stateCode && a.pincode)
        .map((a) => ({
          addressType: a.addressType,
          label: a.label || 'PRIMARY',
          addressLine1: a.addressLine1.trim(),
          addressLine2: a.addressLine2 ? a.addressLine2.trim() : undefined,
          city: a.city.trim(),
          district: a.district ? a.district.trim() : undefined,
          stateCode: a.stateCode.trim(),
          pincode: a.pincode.trim(),
          isPrimary: a.isPrimary,
        }));

      await createLeadMutation.mutateAsync({
        source,
        ownerId: ownerId || undefined,
        partyId: leadMode === 'EXISTING_PARTY' ? partyId.trim() : undefined,
        prospect:
          leadMode === 'NEW_PROSPECT'
            ? {
                name: prospectName.trim(),
                mobile: primaryPhone.trim().replace(/\D/g, ''),
                email: primaryEmail,
                gender: (prospectGender as 'M' | 'F' | 'OTHER') || undefined,
                dob: prospectDob || undefined,
              }
            : undefined,
        productInterest: productCategory
          ? {
              productCategory,
              productInstanceId: productInstanceId || undefined,
              expectedValuePaise: expectedPaise && !isNaN(expectedPaise) ? expectedPaise : undefined,
            }
          : undefined,
        initialNote: initialNote.trim() || undefined,
        followUpDate: followUpDate || undefined,
        contacts: validContacts.length > 0 ? validContacts : undefined,
        addresses: validAddresses.length > 0 ? validAddresses : undefined,
      });

      void navigate({ to: backTo as string });
      // allow-silent-catch: surface error message to UI state
    } catch (err: unknown) {
      setError(getErrorMessage(toAppError(err)));
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="Create Sales Lead / Prospect"
        subtitle="Record a new prospect inquiry or sales lead with contact and product interest details"
      />

      {error && (
        <div
          role="alert"
          className="error p-4 text-sm text-danger-500 bg-danger-500/10 border border-danger-500/20 rounded-md"
        >
          {error}
        </div>
      )}

      {/* Mode Selection Tabs */}
      <div className="flex space-x-2 border-b border-border-base pb-3">
        <Button
          type="button"
          variant={leadMode === 'NEW_PROSPECT' ? 'primary' : 'ghost'}
          onClick={() => setLeadMode('NEW_PROSPECT')}
        >
          + New Prospect Lead
        </Button>
        <Button
          type="button"
          variant={leadMode === 'EXISTING_PARTY' ? 'primary' : 'ghost'}
          onClick={() => setLeadMode('EXISTING_PARTY')}
        >
          Existing Customer / Party
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {leadMode === 'NEW_PROSPECT' ? 'Prospect Personal Information' : 'Select Existing Customer'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {leadMode === 'NEW_PROSPECT' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Full Name <span className="text-danger-500">*</span>
                    </label>
                    <Input
                      type="text"
                      required
                      value={prospectName}
                      onChange={(e) => setProspectName(e.target.value)}
                      placeholder="e.g. Ramesh Kumar"
                    />
                  </div>

                  <div className="col-span-1 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Gender</label>
                      <select
                        value={prospectGender}
                        onChange={(e) => setProspectGender(e.target.value as 'M' | 'F' | 'OTHER' | '')}
                        className="w-full p-2.5 text-sm border border-border-base rounded-md focus:ring-2 focus:ring-accent-500"
                      >
                        <option value="">Select</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Date of Birth</label>
                      <Input
                        type="date"
                        value={prospectDob}
                        onChange={(e) => setProspectDob(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <DynamicContactList
                    contacts={contacts}
                    onChange={(newContacts) => setContacts(newContacts)}
                  />
                </div>

                <div className="border-t pt-4">
                  <DynamicAddressList
                    addresses={addresses}
                    onChange={(newAddrs) => setAddresses(newAddrs)}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Existing Customer / Party <span className="text-danger-500">*</span>
                </label>
                <CustomerPicker
                  value={partyId}
                  onChange={setPartyId}
                  placeholder="Search by customer name or phone..."
                />
              </div>
            )}

            <div className="pt-4 border-t border-border-base space-y-4">
              <h3 className="text-sm font-semibold text-text-primary">Lead Source & Staff Assignment</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Lead Source <span className="text-danger-500">*</span>
                  </label>
                  <select
                    value={source}
                    required
                    onChange={(e) => setSource(e.target.value)}
                    className="w-full p-2.5 text-sm border border-border-base rounded-md focus:ring-2 focus:ring-accent-500"
                  >
                    <option value="">-- Select Lead Source * --</option>
                    <option value="WALK_IN">Branch Walk-in</option>
                    <option value="INBOUND_CALL">Inbound Phone Call</option>
                    <option value="AGENT_REFERRAL">Field Agent / DSA Referral</option>
                    <option value="CUSTOMER_REFERRAL">Customer Referral</option>
                    <option value="CAMP">Camp / Event Outreach</option>
                    <option value="DIGITAL">Digital / Website / Online Ad</option>
                    <option value="DIRECT_OUTREACH">Direct Field Outreach</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Assigned Staff / Owner
                  </label>
                  <select
                    value={ownerId}
                    onChange={(e) => setOwnerId(e.target.value)}
                    className="w-full p-2.5 text-sm border border-border-base rounded-md focus:ring-2 focus:ring-accent-500"
                  >
                    <option value="">-- Unassigned --</option>
                    {staffList.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.name} {staff.id === currentUserId ? '(You)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Next Follow-up Date (Optional)
                  </label>
                  <Input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                  />
                </div>
              </div>

              <h3 className="text-sm font-semibold text-text-primary pt-2 border-t border-border-base">
                Product Interest (Optional)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Interested Product</label>
                  <select
                    value={productCategory}
                    onChange={(e) => {
                      setProductCategory(e.target.value);
                      if (e.target.value !== 'CHIT_FUNDS') setProductInstanceId('');
                    }}
                    className="w-full p-2.5 text-sm border border-border-base rounded-md focus:ring-2 focus:ring-accent-500"
                  >
                    <option value="">-- None / General Inquiry --</option>
                    <option value="CHIT_FUNDS">Chit Fund Scheme</option>
                    <option value="GOLD_LOAN">Gold Loan</option>
                    <option value="FIXED_DEPOSIT">Fixed / Recurring Deposit</option>
                    <option value="GENERAL_INQUIRY">General Financial Inquiry</option>
                  </select>
                </div>

                {productCategory === 'CHIT_FUNDS' ? (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Specific Scheme (Optional)</label>
                    <select
                      value={productInstanceId}
                      onChange={(e) => setProductInstanceId(e.target.value)}
                      className="w-full p-2.5 text-sm border border-border-base rounded-md focus:ring-2 focus:ring-accent-500"
                    >
                      <option value="">-- Any Active Scheme / Undecided --</option>
                      {schemes.map((scheme) => (
                        <option key={scheme.id} value={scheme.id}>
                          {scheme.schemeNumber} - {scheme.displayName} (INR {(scheme.chitValuePaise / 100).toLocaleString('en-IN')})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Target Value (Rupees)</label>
                  <Input
                    type="number"
                    min={0}
                    value={expectedValueRupees}
                    onChange={(e) => setExpectedValueRupees(e.target.value)}
                    placeholder="e.g. 100000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Initial Interaction Notes / Discussion Details
                </label>
                <textarea
                  value={initialNote}
                  onChange={(e) => setInitialNote(e.target.value)}
                  placeholder="Record summary of customer inquiry, key requirements, or discussion points..."
                  className="w-full h-24 p-2.5 text-sm border border-border-base rounded-md focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-border-base">
              <Button type="button" variant="secondary" onClick={() => void navigate({ to: backTo as string })}>
                Cancel
              </Button>
              <Button type="submit" disabled={createLeadMutation.isPending}>
                {createLeadMutation.isPending ? 'Creating Lead...' : 'Create Lead'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
